/**
 * @krisspy-file
 * @type component
 * @name "TouchpointCard"
 * @description "Karta touchpointu s editovatelným názvem, přepínačem vlivu a mazáním. Drag handle icon."
 */

import React, { useState, useRef, useEffect } from 'react';
import { Trash2, GripVertical, Pencil, Check, X } from 'lucide-react';

interface TouchpointCardProps {
  id: string;
  title: string;
  type: 'digital' | 'physical';
  canInfluence: boolean;
  phaseColor?: string;  // e.g. 'border-red-400'
  phaseBg?: string;     // e.g. 'bg-red-100'
  onToggleInfluence: (id: string) => void;
  onDelete: (id: string) => void;
  onRename?: (id: string, newTitle: string) => void;
  dragHandleProps?: Record<string, unknown>;
}

// Extract a Tailwind color name from a border class like 'border-red-400' → 'red'
function extractColor(borderClass = ''): string {
  const match = borderClass.match(/border-(\w+)-\d+/);
  return match?.[1] ?? 'indigo';
}

// Map color name to Tailwind classes
const colorMap: Record<string, { border: string; bg: string; dot: string; btn: string; hatch: string }> = {
  red:    { border: 'border-red-300',    bg: 'bg-red-50',    dot: 'bg-red-400',    btn: 'bg-red-600 text-white',    hatch: 'bg-red-50' },
  orange: { border: 'border-orange-300', bg: 'bg-orange-50', dot: 'bg-orange-400', btn: 'bg-orange-600 text-white', hatch: 'bg-orange-50' },
  green:  { border: 'border-green-300',  bg: 'bg-green-50',  dot: 'bg-green-400',  btn: 'bg-green-600 text-white',  hatch: 'bg-green-50' },
  teal:   { border: 'border-teal-300',   bg: 'bg-teal-50',   dot: 'bg-teal-400',   btn: 'bg-teal-600 text-white',   hatch: 'bg-teal-50' },
  blue:   { border: 'border-blue-300',   bg: 'bg-blue-50',   dot: 'bg-blue-400',   btn: 'bg-blue-600 text-white',   hatch: 'bg-blue-50' },
  indigo: { border: 'border-indigo-300', bg: 'bg-indigo-50', dot: 'bg-indigo-400', btn: 'bg-indigo-600 text-white', hatch: 'bg-indigo-50' },
  emerald:{ border: 'border-emerald-300',bg: 'bg-emerald-50',dot: 'bg-emerald-400',btn: 'bg-emerald-600 text-white',hatch: 'bg-emerald-50' },
};

const TouchpointCard: React.FC<TouchpointCardProps> = ({
  id,
  title,
  type,
  canInfluence,
  phaseColor,
  phaseBg,
  onToggleInfluence,
  onDelete,
  onRename,
  dragHandleProps,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(title);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const commitEdit = () => {
    const trimmed = draft.trim();
    if (trimmed && trimmed !== title) onRename?.(id, trimmed);
    else setDraft(title);
    setEditing(false);
  };

  const cancelEdit = () => { setDraft(title); setEditing(false); };

  const colorName = extractColor(phaseColor);
  const colors = colorMap[colorName] ?? colorMap.indigo;

  return (
    <div
      className={`rounded-xl border-2 p-3 transition-all duration-150 shadow-sm group relative overflow-hidden
        ${canInfluence ? `${colors.border} ${colors.bg}` : `${colors.border} ${colors.bg}`}`}
    >
      {/* Hatching overlay for "cannot influence" */}
      {!canInfluence && (
        <div
          className="absolute inset-0 rounded-xl pointer-events-none"
          style={{
            backgroundImage: 'repeating-linear-gradient(45deg, rgba(0,0,0,0.07) 0px, rgba(0,0,0,0.07) 1px, transparent 1px, transparent 8px)',
          }}
        />
      )}

      <div className="relative flex items-start gap-2">
        {/* Drag handle */}
        <span
          {...(dragHandleProps ?? {})}
          className="mt-0.5 cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500 transition-colors flex-shrink-0"
          title="Přetáhnout"
        >
          <GripVertical className="w-4 h-4" />
        </span>

        {/* Title / edit input */}
        <div className="flex-1 min-w-0">
          {editing ? (
            <div className="flex items-center gap-1">
              <input
                ref={inputRef}
                value={draft}
                onChange={e => setDraft(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') commitEdit();
                  if (e.key === 'Escape') cancelEdit();
                }}
                className="flex-1 text-sm font-medium text-gray-800 bg-white border border-gray-300 rounded px-2 py-0.5 outline-none focus:ring-2 focus:ring-indigo-400"
              />
              <button onClick={commitEdit} className="text-emerald-600 hover:text-emerald-700" title="Uložit">
                <Check className="w-4 h-4" />
              </button>
              <button onClick={cancelEdit} className="text-gray-400 hover:text-gray-600" title="Zrušit">
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="flex items-start gap-1.5 min-w-0">
              <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1.5 ${colors.dot}`} />
              <span className="text-sm font-semibold text-gray-800 leading-snug break-words">{title}</span>
              {onRename && (
                <button
                  onClick={() => setEditing(true)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity ml-0.5 text-gray-400 hover:text-gray-600 flex-shrink-0 mt-0.5"
                  title="Přejmenovat"
                >
                  <Pencil className="w-3 h-3" />
                </button>
              )}
            </div>
          )}
        </div>

        {/* Delete */}
        <button
          onClick={() => onDelete(id)}
          className="opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 text-gray-300 hover:text-red-500 mt-0.5"
          title="Smazat"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Influence toggle */}
      <div className="relative mt-2.5 flex justify-end">
        <button
          onClick={() => onToggleInfluence(id)}
          className={`text-xs font-semibold px-2.5 py-1 rounded-lg transition-all ${
            canInfluence
              ? colors.btn
              : 'bg-white text-gray-600 border border-gray-300'
          }`}
        >
          {canInfluence ? 'Mohu ovlivnit' : 'Nemohu ovlivnit'}
        </button>
      </div>
    </div>
  );
};

export default TouchpointCard;
